// Monolane game engine: rules, combat, bots and the fixed 60 Hz step.
// No DOM, no network. The server runs one engine per match; offline practice runs one in the browser.
export const TAU = Math.PI * 2, hyp = Math.hypot, DT = 1 / 60;
export const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
export const rnd = (a, b) => a + Math.random() * (b - a);
export const gauss = () => { let u = 0, v = 0; while (!u) u = Math.random(); while (!v) v = Math.random(); return Math.sqrt(-2 * Math.log(u)) * Math.cos(TAU * v); };
export const shuffle = a => { for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; };
export const dist = (a, b) => hyp(a.x - b.x, a.y - b.y);
export const fmtT = t => String(Math.floor(t / 60)).padStart(2, '0') + ':' + String(Math.floor(t % 60)).padStart(2, '0');
export const fmtK = n => n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(Math.round(n));

/* ---------- world + rules ---------- */
export const MH = 3000, VH = 1250, LX0 = 160, LX1 = 840;
export const HATCH = [{ x0: 190, x1: 330, y0: 1260, y1: 1740 }, { x0: 670, x1: 810, y0: 1260, y1: 1740 }];
export const WELL = [{ x: 500, y: 2920 }, { x: 500, y: 80 }];
export const WELL_R = 170, LATE_HEAL = 300, SIEGE = 420, SIEGE2 = 600;
export const SIGHT = { champ: 560, melee: 380, ranged: 380, tower: 500, core: 500 };
export const LVL_XP = [0, 90, 200, 330, 480, 650];
export const CLASSES = {
  dart: { key: 'dart', label: 'DART', shape: 'tri', role: 'RANGED DAMAGE', hp: 570, hpL: 65, ad: 50, adL: 6, range: 300, as: 0.85, speed: 330, melee: false,
    q: 'LANCE', e: 'NOVA', qcd: 5, ecd: 13, qRange: 650, qSpeed: 1250, qW: 22, qTxt: 'LONG SKILLSHOT', eTxt: 'BURST AROUND YOU', stat: 'HEALTH 570 · RANGE 300' },
  block: { key: 'block', label: 'BLOCK', shape: 'sq', role: 'FRONTLINE', hp: 950, hpL: 115, ad: 66, adL: 8, range: 95, as: 0.85, speed: 350, melee: true,
    q: 'SHOVE', e: 'BRACE', qcd: 6, ecd: 14, qRange: 320, qSpeed: 1500, qW: 30, qTxt: 'KNOCK BACK AND SLOW', eTxt: 'TEAM TAKES 40% LESS DAMAGE', stat: 'HEALTH 950 · MELEE · HITS HALVE HEALING' },
  ring: { key: 'ring', label: 'RING', shape: 'circ', role: 'SUPPORT · CONTROL', hp: 760, hpL: 84, ad: 52, adL: 5, range: 260, as: 0.9, speed: 330, melee: false,
    q: 'MEND', e: 'FIELD', qcd: 5, ecd: 13, qRange: 600, qSpeed: 1150, qW: 24, qTxt: 'HEAL AN ALLY OR DRAIN AN ENEMY', eTxt: 'ZONE: HEALS, BLOCKS SHOTS, SLOWS', stat: 'HEALTH 760 · RANGE 260' }
};
export const CLS_KEYS = ['dart', 'block', 'ring'];
export const E_RAD = { dart: [210, 270, 320], block: [260, 300, 340], ring: [200, 240, 280] };
export const SHARD_TXT = { dart: ['E +70 · RADIUS 270', 'E +150 · R 320 · 10 S'], block: ['BRACE 45% · R 300', 'BRACE 50% · R 340 · 11 S'], ring: ['FIELD +35% · R 240', 'FIELD +70% · R 280 · 10 S'] };
export const ITEMS = [
  { key: 'blade', label: 'BLADE', code: 'BL', cost: 300, up: 400, t1: '+18 ATK', t2: '+40 ATK' },
  { key: 'quick', label: 'QUICK', code: 'QU', cost: 300, up: 400, t1: '+35% ATK SPEED', t2: '+70% ATK SPEED' },
  { key: 'leech', label: 'LEECH', code: 'LE', cost: 350, up: 400, t1: 'ATTACKS HEAL 15%', t2: 'ATTACKS HEAL 30%' },
  { key: 'plate', label: 'PLATE', code: 'PL', cost: 300, up: 400, t1: '+220 HP', t2: '+480 HP' },
  { key: 'focus', label: 'FOCUS', code: 'FO', cost: 350, up: 450, t1: 'Q +40% · CD -30%', t2: 'Q +75% · CD -40%' },
  { key: 'spring', label: 'SPRING', code: 'SP', cost: 300, up: 350, t1: 'W 5.5 S CD · 320', t2: 'W 4 S CD · 380' },
  { key: 'shard', label: 'SHARD', code: 'SH', cost: 350, up: 450, t1: '', t2: '' },
  { key: 'boots', label: 'BOOTS', code: 'BO', cost: 250, up: 300, t1: '+45 SPEED', t2: '+90 SPEED' }
];
export const ITEM = Object.fromEntries(ITEMS.map(i => [i.key, i]));
export const SLOTS = 4;
export const BUILDS = {
  dart: [['blade', 'quick', 'leech', 'boots'], ['focus', 'shard', 'blade', 'boots']],
  block: [['plate', 'leech', 'spring', 'blade'], ['plate', 'shard', 'boots', 'quick']],
  ring: [['focus', 'shard', 'plate', 'boots'], ['focus', 'spring', 'plate', 'leech']]
};
export const descOf = (it, tier, c) => it.key === 'shard' ? SHARD_TXT[c.cls.key][tier - 1] : it['t' + tier];
export const CONCEDE_AT = { 1: 240, 2: 300, 3: 360 };   // seconds into the match before conceding opens
export const BOT_TAKEOVER = 30;                         // seconds disconnected before a bot plays your seat
export const LEAVE_SECS = 180;                          // total seconds away that count as leaving

export function createEngine(hooks = {}) {
  /* ---------- state ---------- */
  let G = null, uid = 0;
  function unit(o) { return Object.assign({ id: ++uid, alive: true, atkCd: 0, target: null, retarget: 0, vx: 0, vy: 0, knock: null, slowUntil: 0, slowAmt: 0, healCut: 0 }, o); }
  function mkStruct(team, y, tier) {
    const hp = [1100, 1400, 2000][tier];
    return unit({ kind: tier === 2 ? 'core' : 'tower', team, x: 500, y, r: tier === 2 ? 56 : 38, hp, maxHp: hp, range: 350, as: 1.0, tier, streak: 0 });
  }
  const tierOf = (c, key) => { for (const e of c.items) if (e.key === key) return e.tier; return 0; };
  function stats(c) {
    const K = c.cls, old = c.maxHp || 0;
    c.maxHp = K.hp + K.hpL * (c.lvl - 1) + [0, 220, 480][tierOf(c, 'plate')];
    c.ad = K.ad + K.adL * (c.lvl - 1) + [0, 18, 40][tierOf(c, 'blade')];
    c.as = K.as / [1, 1.35, 1.7][tierOf(c, 'quick')];
    c.speed = K.speed + [0, 45, 90][tierOf(c, 'boots')];
    c.range = K.range;
    if (old && c.maxHp > old) c.hp += c.maxHp - old;
    if (c.hp > c.maxHp) c.hp = c.maxHp;
  }
  const qMult = c => [1, 1.4, 1.75][tierOf(c, 'focus')];
  const cdOf = (c, key) => key === 'q' ? c.cls.qcd * [1, 0.7, 0.6][tierOf(c, 'focus')] : key === 'w' ? [9, 5.5, 4][tierOf(c, 'spring')] : (tierOf(c, 'shard') === 2 ? Math.round(c.cls.ecd * 0.77) : c.cls.ecd);
  function qPower(c) {
    const L = c.lvl, ad = c.ad;
    if (c.cls.key === 'dart') return (65 + 20 * L + 0.5 * ad) * qMult(c);
    if (c.cls.key === 'block') return (40 + 14 * L + 0.5 * ad) * qMult(c);
    return (55 + 20 * L + 0.45 * ad) * qMult(c);
  }
  const mendHeal = c => (35 + 12 * c.lvl) * qMult(c);
  const wDist = c => [240, 320, 380][tierOf(c, 'spring')];
  const eRad = c => E_RAD[c.cls.key][tierOf(c, 'shard')];
  const novaDmg = c => 90 + 24 * c.lvl + 0.4 * c.ad + [0, 70, 150][tierOf(c, 'shard')];
  const braceRed = c => [0.4, 0.45, 0.5][tierOf(c, 'shard')];
  const fieldMult = c => [1, 1.35, 1.7][tierOf(c, 'shard')];
  const leechOf = c => [0, 0.15, 0.3][tierOf(c, 'leech')];
  const mkStats = () => ({ k: 0, d: 0, a: 0, cs: 0, gold: 0, dmgChamp: 0, dmgStruct: 0, dmgTaken: 0, heal: 0, qCast: 0, qHit: 0, qChamp: 0, dead: 0 });
  function spawnPos(c) { const f = WELL[c.team]; return { x: f.x + (c.idx - (G.mode - 1) / 2) * 80, y: f.y + (c.team ? 40 : -40) }; }
  function mkChamp(team, idx, bot, clsKey) {
    const cls = CLASSES[clsKey], builds = BUILDS[clsKey];
    const c = unit({ kind: 'champ', team, idx, bot, cls, x: 0, y: 0, r: 22, lvl: 1, xp: 0, gold: 300, items: [], build: builds[Math.floor(Math.random() * builds.length)],
      cd: { q: 0, w: 0, e: 0 }, dead: 0, face: { x: 0, y: team ? 1 : -1 }, move: null, atk: null, dash: null, cast: null, brace: null,
      drewTower: -9, hitBy: -9, hitBySrc: null, dmgBy: {}, range: cls.range, hurtT: -9, retreat: false, think: Math.random() * 0.1, st: mkStats() });
    stats(c); c.hp = c.maxHp;
    return c;
  }
  function pickTeam(n, first, given) {
    if (given) return given.slice(0, n);
    const out = first ? [first] : [];
    const pool = shuffle(CLS_KEYS.filter(x => !out.includes(x)));
    while (out.length < n) out.push(pool.length ? pool.shift() : CLS_KEYS[Math.floor(Math.random() * 3)]);
    return out;
  }
  // roster: [[{cls, human, name}], [{cls, human, name}]]; a missing/null cls is filled with a complementary class
  function newMatch(mode, roster) {
    uid = 0;
    G = { phase: 'play', mode, t: 0, units: [], projs: [], fields: [], fx: [], texts: [], waveT: 4, waveN: 0, over: null, endReason: null, kills: [0, 0], notice: null };
    G.struct = [[mkStruct(0, 2050, 0), mkStruct(0, 2500, 1), mkStruct(0, 2850, 2)], [mkStruct(1, 950, 0), mkStruct(1, 500, 1), mkStruct(1, 150, 2)]];
    G.champs = [];
    for (const team of [0, 1]) {
      const seats = (roster && roster[team]) || [];
      const fixed = seats.map(s => s && s.cls).filter(k => CLASSES[k]);
      const fill = shuffle(CLS_KEYS.filter(k => !fixed.includes(k)));
      for (let i = 0; i < mode; i++) {
        const s = seats[i] || {};
        const key = CLASSES[s.cls] ? s.cls : (fill.length ? fill.shift() : CLS_KEYS[Math.floor(Math.random() * 3)]);
        const c = mkChamp(team, i, !s.human, key);
        c.human = !!s.human; c.name = s.name || null; c.input = { vec: null };
        G.champs.push(c);
      }
    }
    for (const c of G.champs) { const p = spawnPos(c); c.x = p.x; c.y = p.y; }
    G.units.push(...G.struct[0], ...G.struct[1], ...G.champs);
    return G;
  }
  // practice: you are team 0 seat 0, bots everywhere else
  function newGame(mode, myCls, foeCls) {
    newMatch(mode, [[{ cls: myCls, human: true, name: 'YOU' }], (foeCls || []).map(cls => ({ cls }))]);
    G.me = G.champs[0];
    return G;
  }

  /* ---------- queries ---------- */
  const isStruct = u => u.kind === 'tower' || u.kind === 'core';
  const isMinion = u => u.kind === 'melee' || u.kind === 'ranged';
  const alive = u => !!u && u.alive && !(u.kind === 'champ' && u.dead > 0);
  const spd = u => u.speed * (G.t < u.slowUntil ? 1 - u.slowAmt : 1);
  function inHatch(u) { for (let i = 0; i < HATCH.length; i++) { const b = HATCH[i]; if (u.x > b.x0 && u.x < b.x1 && u.y > b.y0 && u.y < b.y1) return i; } return -1; }
  function visible(team, u) {
    if (!alive(u)) return false;
    if (u.team === team || isStruct(u)) return true;
    const br = inHatch(u);
    for (const a of G.units) {
      if (a.team !== team || !alive(a)) continue;
      const d = dist(a, u);
      if (br >= 0) { if (d < 130 || inHatch(a) === br) return true; continue; }
      if (d <= SIGHT[a.kind]) return true;
    }
    return false;
  }
  function targetable(u) {
    if (!alive(u)) return false;
    if (!isStruct(u)) return true;
    return u.tier === 0 || !G.struct[u.team][u.tier - 1].alive;
  }
  const validFor = (team, u) => !!u && u.team !== team && targetable(u) && visible(team, u);
  const atWell = c => dist(c, WELL[c.team]) < WELL_R;
  const canShop = c => c.dead > 0 || atWell(c);
  function wellRate(team) {
    if (!G.struct[team][0].alive && !G.struct[team][1].alive) return 0;
    return G.t > LATE_HEAL ? 0.015 : 0.14;
  }
  function coveringTower(u, team) {
    for (const s of G.struct[team]) if (s.alive && s.kind === 'tower' && dist(s, u) <= s.range + u.r) return s;
    return null;
  }
  const edge = (a, b) => dist(a, b) - a.r - b.r;
  function nearestFoeChamp(c, maxD) {
    let best = null, bd = maxD == null ? Infinity : maxD;
    for (const e of G.champs) if (e.team !== c.team && validFor(c.team, e)) { const d = dist(c, e); if (d < bd) { bd = d; best = e; } }
    return best;
  }

  /* ---------- economy ---------- */
  function say(c, x, y, s) { if (c.human) G.texts.push({ who: c.id, x, y, s, age: 0 }); }
  function earn(c, g) { c.gold += g; c.st.gold += g; }
  function shopAct(c, key) {
    if (!canShop(c)) return false;
    const it = ITEM[key], cur = c.items.find(e => e.key === key);
    if (!it) return false;
    if (!cur) {
      if (c.items.length >= SLOTS || c.gold < it.cost) return false;
      c.gold -= it.cost; c.items.push({ key, tier: 1 });
    } else if (cur.tier === 1) {
      if (c.gold < it.up) return false;
      c.gold -= it.up; cur.tier = 2;
    } else return false;
    stats(c);
    say(c, c.x, c.y - 50, it.label + (tierOf(c, key) === 2 ? ' II' : ''));
    return true;
  }
  const sellValue = e => Math.floor((ITEM[e.key].cost + (e.tier === 2 ? ITEM[e.key].up : 0)) * 0.5);
  function sell(c, key) {
    if (!canShop(c)) return false;
    const i = c.items.findIndex(e => e.key === key);
    if (i < 0) return false;
    const v = sellValue(c.items[i]);
    c.items.splice(i, 1); c.gold += v; stats(c);
    say(c, c.x, c.y - 50, '+' + v);
    return true;
  }
  function nextPurchase(b) {
    for (const key of b.build) if (!tierOf(b, key)) return { key, cost: ITEM[key].cost };
    for (const key of b.build) if (tierOf(b, key) === 1) return { key, cost: ITEM[key].up };
    return null;
  }
  function botBuy(b) { let n; while ((n = nextPurchase(b)) && b.gold >= n.cost) if (!shopAct(b, n.key)) break; }

  /* ---------- combat ---------- */
  function heal(u, amt, src) {
    if (!alive(u) || !(amt > 0)) return 0;
    if (u.healCut > G.t) amt *= 0.5;
    if (G.t > SIEGE2) amt *= 0.5;
    const h = Math.min(u.maxHp - u.hp, amt);
    if (h > 0) { u.hp += h; if (src && src.st) src.st.heal += h; }
    return h;
  }
  function damage(u, dmg, src) {
    if (!alive(u)) return;
    if (u.kind === 'champ') {
      let red = 0;
      for (const b of G.champs) if (b.team === u.team && b.brace && b.brace.until > G.t && alive(b) && dist(b, u) <= b.brace.r) red = Math.max(red, b.brace.red);
      dmg *= 1 - red;
      if (u.cls.key === 'block' && src && src.kind !== 'champ') dmg *= 0.75;
    }
    const dealt = Math.min(dmg, Math.max(0, u.hp));
    u.hp -= dmg;
    if (src && src.kind === 'champ') {
      if (u.kind === 'champ') src.st.dmgChamp += dealt;
      else if (isStruct(u)) src.st.dmgStruct += dealt;
    }
    if (u.kind === 'champ') {
      u.st.dmgTaken += dealt; u.hurtT = G.t;
      if (src && src.kind === 'champ') { src.drewTower = G.t + 2.5; u.hitBy = G.t; u.hitBySrc = src; u.dmgBy[src.id] = G.t; }
    }
    if (u.hp <= 0) kill(u, src);
  }
  function knock(u, vx, vy, d) {
    if (isStruct(u) || !alive(u)) return;
    const L = hyp(vx, vy) || 1;
    u.knock = { vx: vx / L * 1100, vy: vy / L * 1100, t: d / 1100 };
    if (u.kind === 'champ') { u.dash = null; u.cast = null; u.move = null; u.drewTower = Math.max(u.drewTower, G.t + 2); }
  }
  function gainXp(c, xp) {
    if (c.dead > 0) return;
    c.xp += xp;
    let lv = 1; for (let i = 0; i < LVL_XP.length; i++) if (c.xp >= LVL_XP[i]) lv = i + 1;
    if (lv > c.lvl) {
      c.lvl = lv; stats(c);
      G.fx.push({ kind: 'ring', x: c.x, y: c.y, r0: 20, r1: 80, age: 0, life: 0.5, tm: c.team });
      say(c, c.x, c.y - 50, 'LEVEL ' + lv);
    }
  }
  function shareXp(team, at, amount, range) {
    const near = G.champs.filter(c => c.team === team && alive(c) && dist(c, at) < range);
    const each = near.length > 1 ? amount * 0.7 : amount;
    near.forEach(c => gainXp(c, each));
  }
  function kill(u, src) {
    if (u.kind === 'champ') {
      u.hp = 0; u.dead = 5 + 1.5 * u.lvl + (G.t > SIEGE ? 4 : 0);
      u.atk = null; u.move = null; u.dash = null; u.cast = null; u.knock = null; u.brace = null;
      let killer = src && src.kind === 'champ' ? src : null;
      if (!killer) {
        let bt = -1;
        for (const c of G.champs) { const t = u.dmgBy[c.id]; if (c.team !== u.team && t != null && G.t - t <= 10 && t > bt) { bt = t; killer = c; } }
      }
      u.st.d++;
      G.kills[1 - u.team]++;
      if (killer) { killer.st.k++; earn(killer, 300); say(killer, killer.x, killer.y - 50, '+300'); }
      for (const c of G.champs) {
        if (c.team === u.team || c === killer) continue;
        const t = u.dmgBy[c.id], assisted = t != null && G.t - t <= 10;
        if (assisted) { c.st.a++; earn(c, 150); say(c, c.x, c.y - 50, 'ASSIST +150'); }
        else if (alive(c) && dist(c, u) < 900) { earn(c, 100); say(c, c.x, c.y - 50, '+100'); }
      }
      u.dmgBy = {};
      shareXp(1 - u.team, u, 140, 900);
      G.fx.push({ kind: 'ring', x: u.x, y: u.y, r0: 20, r1: 140, age: 0, life: 0.6, tm: -1 });
      return;
    }
    u.alive = false;
    if (isMinion(u)) {
      const g = u.kind === 'melee' ? 21 : 16;
      if (src && src.kind === 'champ') { earn(src, g); src.st.cs++; say(src, u.x, u.y - 24, '+' + g); }
      shareXp(1 - u.team, u, u.kind === 'melee' ? 30 : 24, 850);
    } else {
      for (const c of G.champs) if (c.team !== u.team) { earn(c, 150); say(c, c.x, c.y - 70, '+150'); }
      G.fx.push({ kind: 'ring', x: u.x, y: u.y, r0: 40, r1: 220, age: 0, life: 0.8, tm: -1 });
      if (u.kind === 'core') endMatch(1 - u.team, 'core');
    }
  }
  function shoot(src, tgt, dmg, speed, size, basic) { G.projs.push({ kind: 'homing', x: src.x, y: src.y, ox: src.x, oy: src.y, tgt, dmg, speed, src, team: src.team, size, basic }); }

  /* ---------- champion actions ---------- */
  function setMove(c, x, y) { c.move = { x: clamp(x, LX0 + c.r, LX1 - c.r), y: clamp(y, 20, MH - 20) }; c.atk = null; }
  function setAtk(c, u) { c.atk = u; c.move = null; }
  const busy = c => c.dead > 0 || !!c.dash || !!c.cast || !!c.knock;
  function castQ(c, tx, ty) {
    if (busy(c) || c.cd.q > 0) return false;
    let dx = tx - c.x, dy = ty - c.y, L = hyp(dx, dy); if (L < 1) { dx = c.face.x; dy = c.face.y; L = 1; }
    const K = c.cls;
    c.cd.q = cdOf(c, 'q'); c.st.qCast++;
    c.face.x = dx / L; c.face.y = dy / L;
    if (K.key === 'block') { shove(c, dx / L, dy / L); return true; }
    G.projs.push({ kind: 'bolt', sub: K.key, ox: c.x, oy: c.y, x: c.x, y: c.y, vx: dx / L * K.qSpeed, vy: dy / L * K.qSpeed, left: K.qRange, w: K.qW, dmg: qPower(c), heal: K.key === 'ring' ? mendHeal(c) : 0, src: c, team: c.team, seen: {} });
    return true;
  }
  function shove(c, ux, uy) {
    let hit = null, best = Infinity;
    for (const u of G.units) {
      if (isStruct(u) || !alive(u) || u.team === c.team) continue;
      const rx = u.x - c.x, ry = u.y - c.y, along = rx * ux + ry * uy, perp = Math.abs(rx * uy - ry * ux);
      if (along < 0 || along > c.cls.qRange + u.r || perp > u.r + c.cls.qW) continue;
      if (along < best) { best = along; hit = u; }
    }
    const reach = hit ? best : c.cls.qRange;
    G.fx.push({ kind: 'shove', x: c.x, y: c.y, ux, uy, len: reach, age: 0, life: 0.22, tm: c.team });
    if (!hit) return;
    c.st.qHit++; if (hit.kind === 'champ') c.st.qChamp++;
    damage(hit, qPower(c), c);
    if (alive(hit)) { knock(hit, ux, uy, 120); hit.slowUntil = G.t + 1.6; hit.slowAmt = 0.5; }
  }
  function castW(c, dx, dy) {
    if (busy(c) || c.cd.w > 0) return false;
    let L = hyp(dx, dy); if (L < 1) { dx = c.face.x; dy = c.face.y; L = hyp(dx, dy) || 1; }
    c.cd.w = cdOf(c, 'w'); c.dash = { vx: dx / L * 1600, vy: dy / L * 1600, t: wDist(c) / 1600 };
    return true;
  }
  function castE(c) {
    if (busy(c) || c.cd.e > 0) return false;
    c.cd.e = cdOf(c, 'e');
    if (c.cls.key === 'dart') c.cast = { t: 0.3 };
    else if (c.cls.key === 'block') { c.brace = { until: G.t + 2.5, r: eRad(c), red: braceRed(c) }; G.fx.push({ kind: 'ring', x: c.x, y: c.y, r0: eRad(c) - 40, r1: eRad(c), age: 0, life: 0.35, tm: c.team, w: 8 }); }
    else G.fields.push({ x: c.x, y: c.y, r: eRad(c), until: G.t + 3, team: c.team, src: c, heal: (14 + 4 * c.lvl) * fieldMult(c), dmg: (25 + 8 * c.lvl) * fieldMult(c) });
    return true;
  }
  function bestAttackTarget(c) {
    const R = c.range + 60, T = c.team;
    const hitter = c.hitBySrc;
    if (G.t - c.hitBy < 3 && validFor(T, hitter) && edge(c, hitter) <= R) return hitter;
    for (const e of G.champs) if (validFor(T, e) && edge(c, e) <= R && e.hp < e.maxHp * 0.35) return e;
    if (c.atk && isStruct(c.atk) && validFor(T, c.atk) && edge(c, c.atk) <= R) return c.atk;
    const mins = G.units.filter(u => isMinion(u) && validFor(T, u) && edge(c, u) <= c.range + 120).sort((a, b) => a.hp - b.hp);
    if (mins.length && mins[0].hp <= c.ad) return mins[0];
    for (const u of G.units) if (isStruct(u) && validFor(T, u) && edge(c, u) <= R) return u;
    if (mins.length) return mins[0];
    const foe = nearestFoeChamp(c, 700);
    if (foe) return foe;
    let best = null, nd = 700;
    for (const u of G.units) if (isMinion(u) && validFor(T, u)) { const d = dist(c, u); if (d < nd) { nd = d; best = u; } }
    return best;
  }
  function lead(c, t, speed) { const tt = dist(c, t) / speed; return { x: t.x + t.vx * tt, y: t.y + t.vy * tt }; }
  function autoAimPoint(c) {
    const K = c.cls;
    if (K.key === 'ring') {
      const hurt = G.champs.filter(a => a.team === c.team && a !== c && alive(a) && dist(a, c) < K.qRange && a.hp < a.maxHp * 0.75).sort((a, b) => a.hp / a.maxHp - b.hp / b.maxHp)[0];
      if (hurt) return lead(c, hurt, K.qSpeed);
    }
    const foe = nearestFoeChamp(c, K.qRange + 60);
    if (foe) return lead(c, foe, K.qSpeed);
    let best = null, nd = K.qRange;
    for (const u of G.units) if (isMinion(u) && validFor(c.team, u)) { const d = dist(c, u); if (d < nd) { nd = d; best = u; } }
    if (best) return { x: best.x, y: best.y };
    return { x: c.x + c.face.x * 300, y: c.y + c.face.y * 300 };
  }

  /* ---------- unit updates ---------- */
  function stepToward(u, tx, ty, speed) {
    const dx = tx - u.x, dy = ty - u.y, L = hyp(dx, dy), s = speed * DT;
    if (L < 0.5) return;
    if (L <= s) { u.x = tx; u.y = ty; } else { u.x += dx / L * s; u.y += dy / L * s; }
    if (u.face) { u.face.x = dx / L; u.face.y = dy / L; }
  }
  function updateChamp(c) {
    if (c.dead > 0) {
      c.dead -= DT; c.st.dead += DT;
      if (c.dead <= 0) { const p = spawnPos(c); c.dead = 0; c.x = p.x; c.y = p.y; c.hp = c.maxHp; c.move = null; c.atk = null; c.retreat = false; }
      return;
    }
    for (const key in c.cd) c.cd[key] = Math.max(0, c.cd[key] - DT);
    c.atkCd = Math.max(0, c.atkCd - DT);
    if (c.brace && c.brace.until <= G.t) c.brace = null;
    earn(c, 1.6 * DT);
    if (atWell(c)) c.hp = Math.min(c.maxHp, c.hp + c.maxHp * wellRate(c.team) * DT);
    if (G.t - c.hurtT > 5) c.hp = Math.min(c.maxHp, c.hp + 4 * DT);
    if (c.knock) { c.x += c.knock.vx * DT; c.y += c.knock.vy * DT; c.knock.t -= DT; if (c.knock.t <= 0) c.knock = null; return; }
    if (c.dash) { c.x += c.dash.vx * DT; c.y += c.dash.vy * DT; c.dash.t -= DT; if (c.dash.t <= 0) c.dash = null; return; }
    if (c.cast) {
      c.cast.t -= DT;
      if (c.cast.t <= 0) {
        c.cast = null;
        const R = eRad(c), dmg = novaDmg(c);
        for (const u of G.units) if (u.team !== c.team && !isStruct(u) && alive(u) && dist(u, c) <= R + u.r) damage(u, dmg, c);
        G.fx.push({ kind: 'ring', x: c.x, y: c.y, r0: 60, r1: R + 10, age: 0, life: 0.35, tm: c.team, w: 10 });
      }
      return;
    }
    const jv = c.human && !c.bot ? c.input.vec : null;
    if (jv && hyp(jv.dx, jv.dy) > 14) {
      const L = hyp(jv.dx, jv.dy);
      c.atk = null; c.move = null;
      stepToward(c, c.x + jv.dx / L * 100, c.y + jv.dy / L * 100, spd(c) * Math.min(1, L / 70));
      return;
    }
    if (c.atk && !validFor(c.team, c.atk)) c.atk = null;
    if (c.atk) {
      const t = c.atk;
      if (edge(c, t) > c.range) stepToward(c, t.x, t.y, spd(c));
      else if (c.atkCd <= 0) {
        c.atkCd = c.as;
        const L = dist(c, t) || 1; c.face.x = (t.x - c.x) / L; c.face.y = (t.y - c.y) / L;
        if (c.cls.melee) {
          damage(t, isStruct(t) ? c.ad * 1.3 : c.ad, c);
          t.healCut = G.t + 2;
          for (const o of G.units) if (o !== t && isMinion(o) && o.team !== c.team && alive(o) && dist(o, t) <= 110) damage(o, c.ad * 0.5, c);
          const lr = leechOf(c); if (lr) heal(c, c.ad * lr, c);
          G.fx.push({ kind: 'slash', x: t.x, y: t.y, age: 0, life: 0.18, tm: c.team });
        } else shoot(c, t, c.ad, 1300, 7, true);
      }
      return;
    }
    if (c.move) { stepToward(c, c.move.x, c.move.y, spd(c)); if (hyp(c.move.x - c.x, c.move.y - c.y) < 2) c.move = null; return; }
    if (!c.input.vec) {   // standing still: hit whatever is in range (players and bots alike)
      let best = null, nd = Infinity;
      for (const u of G.units) if ((isMinion(u) || isStruct(u)) && validFor(c.team, u) && edge(c, u) <= c.range) { const d = isStruct(u) ? 9999 : dist(c, u); if (d < nd) { nd = d; best = u; } }
      if (best) c.atk = best;
    }
  }
  function acquireMinion(u) {
    let best = null, bd = Infinity, pri = 9;
    for (const e of G.units) {
      if (e.team === u.team || !targetable(e)) continue;
      const d = dist(u, e);
      if (d > 300 + (isStruct(e) ? e.r : 0)) continue;
      let p;
      if (e.kind === 'champ') p = (e.drewTower > G.t && G.champs.some(a => a.team === u.team && alive(a) && dist(a, u) < 600)) ? 0 : 3;
      else if (isStruct(e)) p = 2;
      else p = 1;
      if (p < pri || (p === pri && d < bd)) { pri = p; bd = d; best = e; }
    }
    return best;
  }
  function updateMinion(u) {
    if (u.knock) { u.x += u.knock.vx * DT; u.y += u.knock.vy * DT; u.knock.t -= DT; if (u.knock.t <= 0) u.knock = null; return; }
    u.atkCd = Math.max(0, u.atkCd - DT);
    u.retarget -= DT;
    if (u.retarget <= 0 || !alive(u.target) || !targetable(u.target)) { u.target = acquireMinion(u); u.retarget = 0.3; }
    const t = u.target;
    if (t) {
      if (edge(u, t) > u.range) stepToward(u, t.x, t.y, spd(u));
      else if (u.atkCd <= 0) {
        u.atkCd = u.as;
        const dmg = isStruct(t) ? u.ad * (G.t > SIEGE2 ? 8 : G.t > SIEGE ? 4 : 2) : u.ad;
        if (u.kind === 'melee') damage(t, dmg, u); else shoot(u, t, dmg, 750, 5);
      }
    } else stepToward(u, u.laneX, u.team ? MH - 100 : 100, spd(u));
  }
  function updateTower(s) {
    if (!s.alive || s.kind === 'core') return;
    s.atkCd = Math.max(0, s.atkCd - DT);
    const inR = e => alive(e) && e.team !== s.team && !isStruct(e) && dist(s, e) <= s.range + e.r;
    const allyNear = G.champs.some(a => a.team === s.team && alive(a) && dist(a, s) < s.range + 200);
    let t = s.target;
    const agg = G.champs.find(e => inR(e) && e.drewTower > G.t && allyNear);
    if (agg) t = agg;
    if (!t || !inR(t)) {
      t = null; let bd = Infinity;
      for (const e of G.units) if (isMinion(e) && inR(e)) { const d = dist(s, e); if (d < bd) { bd = d; t = e; } }
      if (!t) for (const e of G.champs) if (inR(e)) { const d = dist(s, e); if (d < bd) { bd = d; t = e; } }
    }
    if (t !== s.target) { s.streak = 0; s.target = t; }
    if (t && s.atkCd <= 0) {
      s.atkCd = s.as;
      const dmg = t.kind === 'champ' ? 150 * (1 + 0.25 * s.streak) : (t.kind === 'melee' ? 130 : 140) * (1 + 0.05 * G.waveN);
      if (t.kind === 'champ') s.streak++;
      shoot(s, t, dmg, 820, 11);
    }
  }
  function blockedByField(p) {
    if (!p.src || p.src.kind !== 'champ') return false;
    for (const f of G.fields) {
      if (f.team === p.team) continue;
      if (hyp(p.x - f.x, p.y - f.y) <= f.r && hyp(p.ox - f.x, p.oy - f.y) > f.r) return true;
    }
    return false;
  }
  function updateProjs() {
    for (const p of G.projs) {
      if (G.fields.length && blockedByField(p)) { p.done = true; G.fx.push({ kind: 'x', x: p.x, y: p.y, age: 0, life: 0.3, tm: p.team }); continue; }
      if (p.kind === 'homing') {
        if (!alive(p.tgt)) { p.done = true; continue; }
        const dx = p.tgt.x - p.x, dy = p.tgt.y - p.y, L = hyp(dx, dy), s = p.speed * DT;
        if (L <= s + p.tgt.r * 0.5) {
          p.done = true; damage(p.tgt, p.dmg, p.src);
          if (p.basic && alive(p.src)) { const lr = leechOf(p.src); if (lr) heal(p.src, p.dmg * lr, p.src); }
        } else { p.x += dx / L * s; p.y += dy / L * s; }
      } else {
        p.x += p.vx * DT; p.y += p.vy * DT; p.left -= hyp(p.vx, p.vy) * DT;
        if (p.left <= 0) { p.done = true; continue; }
        for (const u of G.units) {
          if (isStruct(u) || !alive(u) || u === p.src) continue;
          const foe = u.team !== p.team;
          if (!foe && !(p.sub === 'ring' && u.kind === 'champ')) continue;
          if (hyp(u.x - p.x, u.y - p.y) > u.r + p.w) continue;
          p.src.st.qHit++;
          if (foe) {
            if (u.kind === 'champ') p.src.st.qChamp++;
            damage(u, p.dmg, p.src);
            if (p.sub === 'ring') heal(p.src, p.dmg * 0.5, p.src);
          } else heal(u, p.heal, p.src);
          p.done = true;
          G.fx.push({ kind: 'ring', x: u.x, y: u.y, r0: 10, r1: 60, age: 0, life: 0.3, tm: p.team });
          break;
        }
      }
    }
    G.projs = G.projs.filter(p => !p.done);
  }
  function updateFields() {
    for (const f of G.fields) {
      for (const u of G.units) {
        if (isStruct(u) || !alive(u) || dist(u, f) > f.r + u.r * 0.5) continue;
        if (u.team === f.team) { if (u.kind === 'champ') heal(u, f.heal * DT, f.src); }
        else { if (!(u.slowUntil > G.t && u.slowAmt > 0.25)) { u.slowUntil = G.t + 0.3; u.slowAmt = 0.25; } damage(u, f.dmg * DT, f.src); }
      }
    }
    G.fields = G.fields.filter(f => f.until > G.t);
  }
  function separate() {
    const mob = G.units.filter(u => alive(u) && !isStruct(u));
    for (let i = 0; i < mob.length; i++) {
      const a = mob[i];
      if (a.kind !== 'champ') for (let j = i + 1; j < mob.length; j++) {
        const b = mob[j];
        if (b.kind === 'champ') continue;
        const d = dist(a, b), m = a.r + b.r;
        if (d < m && d > 0.01) { const push = (m - d) * 0.25, nx = (b.x - a.x) / d, ny = (b.y - a.y) / d; a.x -= nx * push; a.y -= ny * push; b.x += nx * push; b.y += ny * push; }
      }
      for (const s of G.units) {
        if (!isStruct(s) || !s.alive) continue;
        const d = dist(a, s), m = a.r + s.r + 2;
        if (d < m) {
          const nx = d > 0.01 ? (a.x - s.x) / d : 1, ny = d > 0.01 ? (a.y - s.y) / d : 0;
          a.x = s.x + nx * m; a.y = s.y + ny * m;
          a.x += (a.x >= s.x ? 1 : -1) * 1.5;
        }
      }
      a.x = clamp(a.x, LX0 + a.r, LX1 - a.r); a.y = clamp(a.y, 20, MH - 20);
    }
  }

  /* ---------- bots (any seat, either team, any class) ---------- */
  const BACK = { dart: 150, block: 60, ring: 210 };
  function botQ(b, t, spread) { const p = lead(b, t, b.cls.qSpeed); return castQ(b, p.x + gauss() * spread, p.y + gauss() * spread); }
  function threatenedStruct(T) {
    for (const s of G.struct[T]) {
      if (!s.alive || !targetable(s)) continue;
      for (const e of G.units) if (e.team !== T && !isStruct(e) && alive(e) && dist(e, s) <= s.range + 80) return s;
    }
    return null;
  }
  function engage(b, y, d) {
    setAtk(b, y);
    const key = b.cls.key;
    if (key === 'dart') {
      if (b.cd.q <= 0 && d < 640) botQ(b, y, 25);
      else if (b.cd.e <= 0 && d < eRad(b) - 20) castE(b);
      else if (b.cd.w <= 0 && d > 360 && d < 560) castW(b, y.x - b.x, y.y - b.y);
    } else if (key === 'block') {
      if (b.cd.w <= 0 && d > 200 && d < 470) castW(b, y.x - b.x, y.y - b.y);
      else if (b.cd.e <= 0 && d < 300) castE(b);
      else if (b.cd.q <= 0 && d < 280) botQ(b, y, 12);
    } else {
      if (b.cd.q <= 0 && d < 580) botQ(b, y, 25);
      else if (b.cd.e <= 0 && d < eRad(b) + 40) castE(b);
    }
  }
  function runHome(b, y, d) {
    const home = WELL[b.team];
    const chased = y && d < 650;
    // dash home: to escape when chased, or just to save time on a long walk
    if (b.cd.w <= 0 && ((chased && d < 260) || (!chased && dist(b, home) > 700))) castW(b, home.x - b.x, home.y - b.y);
    setMove(b, home.x, home.y);
  }
  function botAI(b) {
    if (b.dead > 0 || busy(b)) return;
    const T = b.team, fwd = T ? 1 : -1, home = WELL[T], key = b.cls.key;
    const hpPct = b.hp / b.maxHp;
    const y = nearestFoeChamp(b, 900), d = y ? dist(b, y) : Infinity;
    const rate = wellRate(T);
    if (rate <= 0) b.retreat = false;
    else if (hpPct < 0.3) b.retreat = true;
    if (b.retreat) {
      const leaveAt = rate >= 0.1 ? 0.95 : (threatenedStruct(T) ? 0.55 : 0.8);
      if (hpPct >= leaveAt) b.retreat = false;
      else {
        b.shopTrip = false;
        if (atWell(b)) {
          if (y && d < 420) engage(b, y, d); else { b.move = null; b.atk = null; }
          return;
        }
        runHome(b, y, d); return;
      }
    }
    // no Return: a bot with spare ink walks home to shop, but turns to fight if someone shows up
    if (b.shopTrip) {
      if (atWell(b) || (y && d < 500)) b.shopTrip = false;
      else { runHome(b, y, d); return; }
    }
    const np = nextPurchase(b);
    if (np && b.gold >= np.cost + 250 && !y && G.t > 60 && dist(b, home) > 800 && !coveringTower(b, 1 - T) && Math.random() < 0.01) { b.shopTrip = true; return; }
    for (const p of G.projs) {
      if (p.kind !== 'bolt' || p.team === T || p.seen[b.id]) continue;
      const sp = hyp(p.vx, p.vy), ux = p.vx / sp, uy = p.vy / sp, rx = b.x - p.x, ry = b.y - p.y;
      const along = rx * ux + ry * uy, perp = rx * uy - ry * ux;
      if (along > 0 && along < 380 && Math.abs(perp) < b.r + p.w + 12) {
        p.seen[b.id] = true;
        if (key === 'ring' && b.cd.e <= 0 && along > 120) { castE(b); return; }
        if (Math.random() < 0.5) {
          const sgn = perp >= 0 ? 1 : -1;
          if (b.cd.w <= 0 && Math.random() < 0.5) castW(b, uy * sgn, -ux * sgn);
          else setMove(b, b.x + uy * sgn * 110, b.y - ux * sgn * 110);
          return;
        }
      }
    }
    if (key === 'ring' && b.cd.q <= 0) {
      const hurt = G.champs.filter(a => a.team === T && a !== b && alive(a) && dist(a, b) < 560 && a.hp < a.maxHp * 0.65).sort((a, c) => a.hp / a.maxHp - c.hp / c.maxHp)[0];
      if (hurt && Math.random() < 0.6) { botQ(b, hurt, 8); return; }
    }
    if (key === 'ring' && b.cd.e <= 0 && y && d < 480 && G.champs.some(a => a.team === T && a !== b && alive(a) && dist(a, b) < 160 && a.hurtT > G.t - 1)) castE(b);
    if (key === 'ring' && b.cd.e <= 0 && y && !y.cls.melee && d <= y.range + 80 && b.hitBy > G.t - 0.5 && hpPct < 0.9) castE(b);
    if (key === 'block' && b.cd.e <= 0 && y && d < 360 && (hpPct < 0.7 || G.champs.some(a => a.team === T && a !== b && alive(a) && dist(a, b) < 240 && a.hurtT > G.t - 0.6))) castE(b);
    const et = coveringTower(b, 1 - T);
    if (et) {
      const tank = G.units.some(u => u.team === T && isMinion(u) && alive(u) && dist(u, et) <= et.range);
      if (!tank || et.target === b) { setMove(b, b.x, b.y - fwd * 220); return; }
    }
    if (y && hpPct > 0.4 && G.struct[T].some(s => s.alive && dist(s, y) < s.range + 150)) { engage(b, y, d); return; }
    if (key === 'block' && y && d < 420 && hpPct > 0.55 && !coveringTower(y, 1 - T)) { engage(b, y, d); return; }
    if (y) {
      const killable = y.hp < b.ad * 2 + qPower(b) || (y.hp / y.maxHp < 0.4 && hpPct > 0.55);
      if (killable && !coveringTower(y, 1 - T)) { engage(b, y, d); return; }
      if (key !== 'block' && b.cd.q <= 0 && d < b.cls.qRange - 40 && Math.random() < 0.12) botQ(b, y, 45);
      if (key === 'dart' && b.cd.e <= 0 && d < eRad(b) - 40 && Math.random() < 0.3) castE(b);
      if (key === 'ring' && b.cd.e <= 0 && d < eRad(b) && Math.random() < 0.2) castE(b);
    }
    const mins = G.units.filter(u => u.team !== T && isMinion(u) && validFor(T, u));
    const lh = mins.filter(m => m.hp <= b.ad * 1.05 && edge(b, m) <= b.range + 90).sort((a, c) => dist(b, a) - dist(b, c))[0];
    if (lh) { setAtk(b, lh); return; }
    if (b.cls.melee) {
      const soon = mins.filter(m => m.hp <= b.ad * 2.2 && edge(b, m) <= 260 && !coveringTower(m, 1 - T)).sort((a, c) => a.hp - c.hp)[0];
      if (soon) {
        if (edge(b, soon) > b.range * 0.7) { const L = dist(b, soon) || 1, stand = b.r + soon.r + b.range * 0.5; setMove(b, soon.x - (soon.x - b.x) / L * stand, soon.y - (soon.y - b.y) / L * stand); }
        else { b.move = null; b.atk = null; }
        return;
      }
    }
    const allies = G.units.filter(u => u.team === T && isMinion(u) && alive(u));
    for (const s of G.struct[1 - T]) {
      if (!s.alive || !targetable(s)) continue;
      const tankers = allies.filter(u => dist(u, s) <= s.range);
      if ((tankers.length >= 2 || s.kind === 'core') && s.target !== b && (!y || d > 450)) { setAtk(b, s); return; }
      break;
    }
    let front = null;
    for (const u of allies) if (front === null || (u.y - front) * fwd > 0) front = u.y;
    let ownFront = home.y;
    for (const s of G.struct[T]) if (s.alive && (s.y - ownFront) * fwd > 0) ownFront = s.y;
    let posY = front !== null ? front - fwd * BACK[key] : ownFront + fwd * 160;
    if (y && d < 420 && hpPct < y.hp / y.maxHp) posY -= fwd * 140;
    const inRange = mins.filter(m => edge(b, m) <= b.range);
    if (inRange.length && Math.random() < 0.35 && !inRange.some(m => m.hp < b.ad * 2.2)) { setAtk(b, inRange.sort((a, c) => a.hp - c.hp)[0]); return; }
    if (b.atk && validFor(T, b.atk) && b.atk.kind !== 'champ') return;
    setMove(b, 500 + (b.idx - (G.mode - 1) / 2) * 110 + Math.sin(G.t * 0.7 + b.id) * 70, clamp(posY, 120, MH - 120));
  }

  /* ---------- main step ---------- */
  function mkMinion(team, kind, i) {
    const sc = 1 + 0.05 * G.waveN, melee = kind === 'melee';
    const y = team ? 250 - i * 34 : 2750 + i * 34;
    return unit({ kind, team, x: 500 + rnd(-40, 40), y, r: melee ? 14 : 13, hp: (melee ? 340 : 250) * sc, maxHp: (melee ? 340 : 250) * sc,
      ad: (melee ? 14 : 22) * sc, as: melee ? 1.0 : 1.25, range: melee ? 30 : 230, speed: 125, laneX: 500 + rnd(-55, 55) });
  }
  function spawnWave() {
    G.waveN++;
    const n = 5 + 2 * (G.mode - 1), melee = Math.ceil(n * 0.6);
    for (const team of [0, 1]) for (let i = 0; i < n; i++) G.units.push(mkMinion(team, i < melee ? 'melee' : 'ranged', i));
  }
  function step() {
    const t0 = G.t;
    G.t += DT;
    if (t0 < LATE_HEAL && G.t >= LATE_HEAL) G.notice = { s: '5:00 · WELLS NOW HEAL SLOWLY', t: G.t };
    if (t0 < SIEGE && G.t >= SIEGE) G.notice = { s: '7:00 · PIPS HIT POSTS 2× HARDER', t: G.t };
    if (t0 < SIEGE2 && G.t >= SIEGE2) G.notice = { s: '10:00 · POSTS TAKE 4× · HEALING HALVED', t: G.t };
    G.waveT -= DT; if (G.waveT <= 0) { spawnWave(); G.waveT = G.t > SIEGE2 ? 15 : G.t > SIEGE ? 20 : 25; }
    for (const c of G.champs) if (c.bot) { c.think -= DT; if (c.think <= 0) { c.think = 0.1; if (canShop(c)) botBuy(c); botAI(c); } }
    const before = new Map(G.units.map(u => [u, { x: u.x, y: u.y }]));
    for (const u of G.units) {
      if (!u.alive) continue;
      if (u.kind === 'champ') updateChamp(u);
      else if (isStruct(u)) updateTower(u);
      else updateMinion(u);
    }
    updateProjs();
    updateFields();
    separate();
    for (const u of G.units) { const p = before.get(u); if (p) { u.vx = (u.x - p.x) / DT; u.vy = (u.y - p.y) / DT; } }
    G.units = G.units.filter(u => u.alive || isStruct(u));
  }
  function endMatch(winner, reason) {
    if (G.phase === 'over') return;
    G.over = winner; G.phase = 'over'; G.endReason = reason; G.endT = 0;
    if (hooks.onEnd) hooks.onEnd(G, winner, reason);
  }
  function concede(team) { if (G.phase !== 'play') return false; endMatch(1 - team, 'concede'); return true; }
  function findUnit(id) { for (const u of G.units) if (u.id === id) return u; return null; }
  // can `team` see the point (x, y)? same rules as visible(), used to filter effects sent to each side
  function visibleAt(team, x, y) {
    const br = inHatch({ x, y });
    for (const a of G.units) {
      if (a.team !== team || !alive(a)) continue;
      const d = hyp(a.x - x, a.y - y);
      if (br >= 0) { if (d < 130 || inHatch(a) === br) return true; continue; }
      if (d <= SIGHT[a.kind]) return true;
    }
    return false;
  }
  const num = v => typeof v === 'number' && isFinite(v);
  // one entry point for every player action, used by both the server and offline practice
  function command(c, m) {
    if (!G || !c || !m || G.phase !== 'play') return;
    switch (m.t) {
      case 'mv': c.input.vec = num(m.dx) && num(m.dy) && (m.dx || m.dy) ? { dx: clamp(m.dx, -90, 90), dy: clamp(m.dy, -90, 90) } : null; break;
      case 'go': if (c.dead <= 0 && num(m.x) && num(m.y)) setMove(c, m.x, m.y); break;
      case 'stop': c.move = null; c.atk = null; c.input.vec = null; break;
      case 'atk': {
        if (c.dead > 0) break;
        const t = m.id != null ? findUnit(m.id) : bestAttackTarget(c);
        if (t && validFor(c.team, t)) setAtk(c, t);
        break;
      }
      case 'q': case 'w': {
        if (c.dead > 0) break;
        let p = num(m.x) && num(m.y) ? { x: m.x, y: m.y } : autoAimPoint(c);
        if (p.x === c.x && p.y === c.y) p = autoAimPoint(c);
        if (m.t === 'q') castQ(c, p.x, p.y); else castW(c, p.x - c.x, p.y - c.y);
        break;
      }
      case 'e': if (c.dead <= 0) castE(c); break;
      case 'buy': if (typeof m.key === 'string') shopAct(c, m.key); break;
      case 'sell': if (typeof m.key === 'string') sell(c, m.key); break;
    }
  }

  return {
    get G() { return G; }, setG(g) { G = g; },
    newMatch, newGame, step, command, concede, endMatch, findUnit, visibleAt,
    isStruct, isMinion, alive, inHatch, visible, targetable, validFor, atWell, canShop, wellRate, coveringTower, edge,
    tierOf, stats, cdOf, qPower, wDist, eRad, sellValue, nextPurchase, autoAimPoint, bestAttackTarget, spawnPos
  };
}
